/**
 * Consolidates orders with the same Vendor ID (customer_id) into a single row.
 *
 * When multiple orders share the same customer_id, they are merged into one
 * consolidated entry showing:
 *  - The final (most progressed) status
 *  - The most recent date
 *  - The correct total, customer info, payment method, and item count
 *
 * Orders with unique customer_ids or no customer_id are left unchanged.
 */

/**
 * Status priority: higher number = more progressed in the order lifecycle.
 * The consolidated row will show the status with the highest priority.
 */
const STATUS_PRIORITY: Record<string, number> = {
  Incomplete: 1,
  Pending: 2,
  'Awaiting Payment': 3,
  'Awaiting Shipment': 4,
  'Awaiting Fulfillment': 5,
  'Partially Shipped': 6,
  Shipped: 7,
  Completed: 8,
  'Partially Refunded': 9,
  Refunded: 10,
  Cancelled: 0,
  Declined: 0,
  Disputed: 0,
};

/** Shape of an order as returned by the API */
export interface Order {
  id: number;
  cart_id?: string;
  date_created: string;
  status: string;
  customer_id: number | string | null;
  total_inc_tax?: string;
  total?: string;
  items_total?: number;
  billing_address?: {
    email?: string;
    first_name?: string;
    last_name?: string;
    [key: string]: unknown;
  };
  customer?: {
    email?: string;
    first_name?: string;
    last_name?: string;
    [key: string]: unknown;
  };
  payment_method?: string;
  payment_provider_id?: string;
  [key: string]: unknown;
}

/** A consolidated order extends the base order with consolidation metadata */
export interface ConsolidatedOrder extends Order {
  /** All individual order IDs that were merged into this row */
  _consolidatedOrderIds: number[];
  /** Whether this row represents multiple merged orders */
  _isConsolidated: boolean;
}

/**
 * Get the priority for a given status string.
 * Unknown statuses default to priority 0.
 */
function getStatusPriority(status: string | undefined | null): number {
  if (!status) {
    return 0;
  }
  return STATUS_PRIORITY[status] ?? 0;
}

/**
 * Select the "final" order from a group — the one with the most progressed status.
 * If two orders share the same status priority, pick the one with the more recent date.
 */
function selectFinalOrder(orders: Order[]): Order {
  return orders.reduce((best, current) => {
    const bestPriority = getStatusPriority(best.status);
    const currentPriority = getStatusPriority(current.status);

    if (currentPriority > bestPriority) {
      return current;
    }
    if (currentPriority === bestPriority) {
      const bestDate = best.date_created ? new Date(best.date_created).getTime() : 0;
      const currentDate = current.date_created ? new Date(current.date_created).getTime() : 0;
      return currentDate > bestDate ? current : best;
    }
    return best;
  });
}

/**
 * Get the most recent date_created from a group of orders.
 */
function getMostRecentDate(orders: Order[]): string {
  return orders.reduce((latest, order) => {
    if (!order.date_created) {
      return latest;
    }
    if (!latest) {
      return order.date_created;
    }
    return new Date(order.date_created) > new Date(latest) ? order.date_created : latest;
  }, '');
}

/**
 * Get the best payment method from a group of orders.
 * Prefers non-empty, non-N/A values from the final order.
 */
function getBestPaymentMethod(orders: Order[], finalOrder: Order): {
  payment_method?: string;
  payment_provider_id?: string;
} {
  // If the final order has a valid payment method, use it
  if (finalOrder.payment_method && finalOrder.payment_method !== 'N/A') {
    return {
      payment_method: finalOrder.payment_method,
      payment_provider_id: finalOrder.payment_provider_id,
    };
  }

  // Otherwise, find the first order with a valid payment method
  const orderWithPayment = orders.find(
    (o) => o.payment_method && o.payment_method !== 'N/A'
  );

  if (orderWithPayment) {
    return {
      payment_method: orderWithPayment.payment_method,
      payment_provider_id: orderWithPayment.payment_provider_id,
    };
  }

  return {
    payment_method: finalOrder.payment_method,
    payment_provider_id: finalOrder.payment_provider_id,
  };
}

/**
 * Consolidate an array of orders by Vendor ID (customer_id).
 *
 * Orders sharing the same customer_id are merged into a single
 * ConsolidatedOrder row. Orders with null/undefined customer_id
 * are never consolidated (each remains its own row).
 *
 * @param orders - Raw orders from the API
 * @returns Array of ConsolidatedOrder objects
 */
export function consolidateOrdersByVendorId(orders: Order[]): ConsolidatedOrder[] {
  const validOrders = Array.isArray(orders)
    ? orders.filter(
        (order) =>
          order &&
          typeof order.id === 'number' &&
          Number.isFinite(order.id),
      )
    : [];

  if (validOrders.length === 0) {
    return [];
  }

  // Group orders by customer_id (Vendor ID)
  const groups = new Map<string, Order[]>();
  const ungrouped: Order[] = [];

  for (const order of validOrders) {
    const vendorId = order.customer_id;

    // Orders without a customer_id cannot be consolidated
    if (vendorId === null || vendorId === undefined || vendorId === '') {
      ungrouped.push(order);
      continue;
    }

    const key = String(vendorId);
    const group = groups.get(key);
    if (group) {
      group.push(order);
    } else {
      groups.set(key, [order]);
    }
  }

  const consolidated: ConsolidatedOrder[] = [];

  // Process each group
  for (const [, groupOrders] of Array.from(groups.entries())) {
    if (groupOrders.length === 1) {
      // Single order — no consolidation needed
      consolidated.push({
        ...groupOrders[0],
        _consolidatedOrderIds: [groupOrders[0].id],
        _isConsolidated: false,
      });
      continue;
    }

    // Multiple orders — consolidate
    const finalOrder = selectFinalOrder(groupOrders);
    const mostRecentDate = getMostRecentDate(groupOrders);
    const payment = getBestPaymentMethod(groupOrders, finalOrder);

    // Collect all order IDs, sorted descending (newest first)
    const allOrderIds = groupOrders
      .map((o: Order) => o.id)
      .sort((a: number, b: number) => b - a);

    // Use the highest order ID as the representative ID for navigation
    const representativeId = allOrderIds[0];

    consolidated.push({
      ...finalOrder,
      id: representativeId,
      date_created: mostRecentDate,
      payment_method: payment.payment_method,
      payment_provider_id: payment.payment_provider_id,
      _consolidatedOrderIds: allOrderIds,
      _isConsolidated: true,
    });
  }

  // Add ungrouped orders (no customer_id)
  for (const order of ungrouped) {
    consolidated.push({
      ...order,
      _consolidatedOrderIds: [order.id],
      _isConsolidated: false,
    });
  }

  return consolidated;
}
